import type { Orden } from "../../lib/orden";

/**
 * La lista unificada de miembros e invitaciones (ADR 0080).
 *
 * Es el `UnifiedMember` de `imprenta`: para quien administra accesos, «Juan que
 * entra» y «Juan que todavía no aceptó» son la misma pregunta, y separarlos en
 * dos listas hace que una invitación olvidada no se vea.
 *
 * Vive aparte porque acá la pantalla puede mentir **sin fallar**: una invitación
 * cancelada contada como pendiente hace que alguien la espere para siempre.
 */

/** Lo mínimo que estas reglas necesitan de cada lado. */
export interface UsuarioDeFila {
  id: string;
  email: string;
  nombre: string;
  activo: boolean;
  roles: string[];
}

export interface InvitacionDeFila {
  id: string;
  email: string;
  rol: string | null;
  estado: string;
  expiraEn: string;
}

/**
 * Los tres estados que la pantalla ofrece filtrar, más `otro`.
 *
 * Una invitación cancelada, expirada o ya aceptada **no es pendiente**: sigue
 * en la lista —porque explica por qué alguien no entró— pero no cuenta como
 * algo que esperar.
 */
export type EstadoDeMiembro = "activo" | "pendiente" | "inactivo" | "otro";

export interface Miembro {
  /** Única en toda la lista: los dos ids salen de tablas distintas. */
  clave: string;
  clase: "usuario" | "invitacion";
  nombre: string;
  email: string;
  roles: string[];
  estado: EstadoDeMiembro;
  usuario: UsuarioDeFila | null;
  invitacion: InvitacionDeFila | null;
}

export function unificarMiembros(
  usuarios: readonly UsuarioDeFila[],
  invitaciones: readonly InvitacionDeFila[],
): Miembro[] {
  return [
    ...usuarios.map((usuario) => ({
      // Prefijo, como allá: sin él, un usuario y una invitación con el mismo
      // id comparten clave y React dibuja una sola fila.
      clave: `u-${usuario.id}`,
      clase: "usuario" as const,
      nombre: usuario.nombre,
      email: usuario.email,
      roles: usuario.roles,
      estado: (usuario.activo ? "activo" : "inactivo") as EstadoDeMiembro,
      usuario,
      invitacion: null,
    })),
    ...invitaciones.map((invitacion) => ({
      clave: `i-${invitacion.id}`,
      clase: "invitacion" as const,
      // Todavía no hay persona: lo único que se sabe es a quién se invitó.
      nombre: invitacion.email,
      email: invitacion.email,
      roles: invitacion.rol ? [invitacion.rol] : [],
      estado: (invitacion.estado === "pending" ? "pendiente" : "otro") as EstadoDeMiembro,
      usuario: null,
      invitacion,
    })),
  ];
}

/** Lo que dicen las cuatro tarjetas que filtran. */
export function contarPorEstado(miembros: readonly Miembro[]): {
  todos: number;
  activo: number;
  pendiente: number;
  inactivo: number;
} {
  // Arranca en cero y no vacío: una tarjeta sin número se lee como que no cargó.
  const cuenta = { todos: miembros.length, activo: 0, pendiente: 0, inactivo: 0 };
  for (const m of miembros) {
    if (m.estado !== "otro") cuenta[m.estado] += 1;
  }
  return cuenta;
}

const ESTADO_EN_ORDEN: Record<EstadoDeMiembro, number> = {
  activo: 0,
  pendiente: 1,
  inactivo: 2,
  otro: 3,
};

/** Ordena una COPIA: la lista que llega se recalcula en cada render. */
export function ordenarMiembros(miembros: readonly Miembro[], orden: Orden | null): Miembro[] {
  if (!orden) return [...miembros];
  const signo = orden.direccion === "desc" ? -1 : 1;

  return [...miembros].sort((a, b) => {
    if (orden.campo === "estado") {
      return (ESTADO_EN_ORDEN[a.estado] - ESTADO_EN_ORDEN[b.estado]) * signo;
    }
    // Quien no tiene rol va al final en las dos direcciones: ordenar por rol es
    // buscar a los de un rol, no a los que no tienen ninguno.
    const x = orden.campo === "rol" ? (a.roles[0] ?? "") : a.nombre;
    const y = orden.campo === "rol" ? (b.roles[0] ?? "") : b.nombre;
    if (x === "") return y === "" ? 0 : 1;
    if (y === "") return -1;
    return x.localeCompare(y, "es", { numeric: true }) * signo;
  });
}
